import React, { createContext, useContext, useEffect, useState } from "react";
import { Avatar, Box, Text } from "@mantine/core";
import moment from "moment-timezone";
import { useCalendar } from "../../../../../context/CalendarContext";
import { useSalon } from "../../../../../context/SalonContext";
import { CalendarResource } from "../../../../../types/calendar";
import { BusinessInfo } from "../../../../../types/context";
import { getInitials, getSalonNow } from "./sharedCalendarUtils";
import type { BlockedStaff } from "../../booking/skillBlock";
import { blockedColumnLabel } from "../../booking/skillBlock";
import styles from "./Views.module.css";

// Module-level RBC parts shared by the Day and Week grids (they go into the
// DAY_COMPONENTS / WEEK_COMPONENTS constants; see rbcComponentStability).

/** Grid step: 15-minute slots (hover target, click and drag snapping). */
export const GRID_STEP_MINS = 15;
/** Slots per timeslot group: one group per hour. */
export const GRID_TIMESLOTS = 4;

/** "2:15 PM" from a grid Date's own (browser-local = salon wall-clock) time. */
export const wallClockLabel = (d: Date): string => {
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h % 12 || 12}:${m} ${h < 12 ? "AM" : "PM"}`;
};

/** Compact hour label ("9 AM") for a grid Date's wall-clock hour. */
export const hourLabel = (d: Date): string => {
  const h = d.getHours();
  return `${h % 12 || 12} ${h < 12 ? "AM" : "PM"}`;
};

/**
 * RBC `timeSlotWrapper`: tags each slot with its "+ 2:15 PM" hover label
 * (CSS shows it on hover inside day columns only, so the gutter's slots
 * ignore it), and each top-of-hour slot with its "9 AM" hour label (CSS
 * shows it in split week's per-staff hour strip only). No extra DOM: the
 * labels are the slot's own ::after / ::before.
 */
export const SlotHoverWrapper = ({
  value,
  children,
}: {
  value: Date;
  children: React.ReactElement;
}) =>
  React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<any>, {
        "data-slot-label": `+ ${wallClockLabel(value)}`,
        "data-hour-label":
          value.getMinutes() === 0 ? hourLabel(value) : undefined,
      })
    : children;

/**
 * Booking drawer: day-view columns whose staff can't do the active service
 * (provided by DayView; read here because the header is an RBC component).
 */
export const BlockedStaffContext = createContext<BlockedStaff | null>(null);

const minuteOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes();

/** The now line's offset down the gutter (%), or null outside the grid. */
export const nowTopPercent = (groups: Date[][], now: Date): number | null => {
  const first = groups[0]?.[0];
  if (!first) return null;
  const total = groups.length * GRID_TIMESLOTS * GRID_STEP_MINS;
  const offset = minuteOfDay(now) - minuteOfDay(first);
  return offset < 0 || offset > total ? null : (offset / total) * 100;
};

/**
 * RBC `timeGutterWrapper`: the current time in a red pill on the gutter,
 * level with the now line (prototype), while today is on screen (the viewed
 * day, or the viewed week in the grid's locale week). Re-renders each minute.
 */
export const NowGutterWrapper = ({
  slotMetrics,
  children,
}: {
  slotMetrics: { groups: Date[][] };
  children: React.ReactElement;
}) => {
  const { selectedDate, selectedView } = useCalendar();
  const { businessInfo } = useSalon() as { businessInfo: BusinessInfo | null };
  const timezone = businessInfo?.timezone;
  const show = moment(selectedDate).isSame(
    getSalonNow(timezone),
    selectedView === "week" ? "week" : "day",
  );
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!show) return undefined;
    const id = window.setInterval(() => setTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, [show]);
  const now = getSalonNow(timezone);
  const top = show ? nowTopPercent(slotMetrics.groups, now) : null;
  if (top === null || !React.isValidElement(children)) return children;
  const gutter = children as React.ReactElement<{ children?: React.ReactNode }>;
  return React.cloneElement(
    gutter,
    undefined,
    gutter.props.children,
    <Box
      component="span"
      key="now"
      className={styles.nowLabel}
      style={{ top: `${top}%` }}
      data-testid="now-label"
    >
      {wallClockLabel(now)}
    </Box>,
  );
};

/** Staff column header: photo (initials fallback) + name. */
export const StaffColumnHeader = ({
  resource,
}: {
  resource: CalendarResource;
}) => {
  const blocked = useContext(BlockedStaffContext);
  const isBlocked = !!blocked?.staffIds.has(String(resource.id));
  return (
    <Box className={styles.staffHeader} data-blocked={isBlocked || undefined}>
      <Avatar
        src={resource.avatar || undefined}
        size={26}
        radius="xl"
        classNames={{ placeholder: styles.staffAvatar }}
        aria-hidden
      >
        {getInitials(`${resource.firstName ?? ""} ${resource.lastName ?? ""}`)}
      </Avatar>
      <Box className={styles.staffHeaderText}>
        <Text
          component="span"
          className={styles.staffName}
          title={resource.title}
        >
          {resource.title}
        </Text>
        {isBlocked && blocked && (
          <Text component="span" className={styles.staffBlockedLabel}>
            {blockedColumnLabel(blocked.serviceName)}
          </Text>
        )}
      </Box>
    </Box>
  );
};

const OPENING_LEAD_MINS = 30;
const WEEKDAYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

/**
 * Where the grid opens: the salon's opening time on the viewed day minus 30
 * minutes (design handoff). Falls back to the current salon hour when the
 * salon has no hours that day.
 */
export const openingScrollTime = (
  businessInfo: BusinessInfo | null | undefined,
  date: Date,
): Date => {
  const day = WEEKDAYS[date.getDay()];
  const open = businessInfo?.[`${day}_open_time` as keyof BusinessInfo];
  if (typeof open === "string" && open) {
    const opening = moment(open, ["h:mm A", "HH:mm"]);
    if (opening.isValid()) {
      const minutes = Math.max(
        0,
        opening.hours() * 60 + opening.minutes() - OPENING_LEAD_MINS,
      );
      const at = new Date(date);
      at.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
      return at;
    }
  }
  const now = getSalonNow(businessInfo?.timezone);
  now.setMinutes(0, 0, 0);
  return now;
};
